import React, { useEffect, useState } from "react";
import parse from "html-react-parser";
import { useTranslation } from "react-i18next";
import { default as i18n } from "@learningblocks/shared-components/lib/i18n";

import "./index.css";

export default function TabComp(props) {
  const { t } = useTranslation("translation");

  const [activeTab, setActiveTab] = useState(0);
  const [imgSrc, setImgSrc] = useState(null);
  const [usedMobile, setUsedMobile] = useState(false);
  const [imgErrorFallback, setImgErrorFallback] = useState(false);

  // 1) Preload all English fallback images in the background
  //    (desktop + mobile) so that when you hit the fallback path, they’re
  //    already cached and show instantly.
  useEffect(() => {
    if (!props.preview && props.data?.tabs) {
      const base = `${window.location.origin}${window.location.pathname}locales/en/images/`;
      props.data.tabs.forEach((tab) => {
        new Image().src = base + tab.img;
        new Image().src = base + tab.mobileImg;
      });
    }
  }, [props.data, props.preview]);

  // 2) Pick the correct image (mobile vs desktop) on mount / tab / bp / locale change
  useEffect(() => {
    const isMobile = !["lg", "xl", "md"].includes(props.bp);
    setUsedMobile(isMobile);
    setImgErrorFallback(false);

    const tab = props.data.tabs[activeTab];
    const basePath = `${window.location.origin}${window.location.pathname}locales/${i18n.language}/images/`;

    const src = props.preview
      ? isMobile
        ? tab.mobileImgURL
        : tab.imgURL
      : isMobile
        ? basePath + tab.mobileImg
        : basePath + tab.img;

    setImgSrc(src);
  }, [activeTab, props.bp, props.preview, props.data, i18n.language]);

  // 3) Reset to first tab whenever the data object entirely changes
  useEffect(() => {
    setActiveTab(0);
  }, [props.data]);

  return (
    <div className="tab-comparison">
      <div className="tabComp-paragraph">
        {parse(t(`${props.data.text}.string`))}
      </div>

      <div id="tabComp-tabs" className="tabComp-tabs">
        {props.data.tabs.map((tab, key) => (
          <div
            role="button"
            key={key}
            className={`tabComp-tab ${
              activeTab === key ? "tabComp-active" : ""
            }`}
            tabIndex="0"
            onClick={() => {
              setActiveTab(key);
            }}
            onKeyUp={(e) => {
              const code = e.keyCode || e.which;
              if (code === 13) {
                setActiveTab(key);
              }
            }}
          >
            {t(`${tab.label}.string`)}
          </div>
        ))}
      </div>

      <div className="tabComp-content">
        <img
          className="tabComp-image"
          src={imgSrc}
          onError={() => {
            const tab = props.data.tabs[activeTab];

            if (usedMobile && !imgErrorFallback) {
              // mobile image failed → fallback to localized desktop
              const fallback = props.preview
                ? tab.imgURL
                : `${window.location.origin}${window.location.pathname}locales/${i18n.language}/images/${tab.img}`;
              setImgSrc(fallback);
              setImgErrorFallback(true);
            } else {
              // final fallback → English desktop
              const finalFallback = props.preview
                ? tab.imgURL
                : `${window.location.origin}${window.location.pathname}locales/en/images/${tab.img}`;
              setImgSrc(finalFallback);
            }
          }}
          alt={
            props.data.tabs[activeTab].imgAlt
              ? t(`${props.data.tabs[activeTab].imgAlt}.string`)
              : ""
          }
          style={{
            minHeight: props.preview ? "100px" : undefined,
            minWidth: props.preview ? "100%" : undefined,
          }}
        />
      </div>
    </div>
  );
}
